import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import DataTable from "examples/Tables/DataTable";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { ToastContainer, toast } from "react-toastify";
import { Stack, CircularProgress } from "@mui/material";
import InputBase from "@mui/material/InputBase";
import Paper from "@mui/material/Paper";
import SearchIcon from "@mui/icons-material/Search";
import Papa from "papaparse";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import MDBadge from "components/MDBadge";
import VisibilityIcon from "@mui/icons-material/Visibility";
import colors from "assets/theme/base/colors";
import "../campus/Campus.css";
import Select from "react-select";
import nodata from "assets/images/nodata.png";
import { hasAdmissionPermission } from "hooks/permissions";

const { info } = colors;
const BASE_URL = process.env.REACT_APP_BASE_URL;
const token = sessionStorage.getItem("token");

const AllowedApplicantReport = () => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const userDepartment = JSON.parse(sessionStorage.getItem("department_id"));
  const [loading, setLoading] = useState(false);

  // Session Storage
  const user = JSON.parse(sessionStorage.getItem("user"));
  const organization_id = user ? user.organization_id : null;

  // Variable Declarations
  const [allApplicants, setAllApplicants] = useState([]);
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [originalApplicants, setOriginalApplicants] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");

  const handleViewApplicant = (userId) => {
    window.open(`/admissions/get-applications/${userId}`);
  };

  const handleSearchKeywordChange = (e) => {
    const searchTerm = e.target.value.trim().toLowerCase();
    setSearchKeyword(searchTerm);
    
    if (!searchTerm) {
      setAllApplicants(originalApplicants);
      return;
    }

    const filteredApplicants = originalApplicants.filter((applicant) => {
      return (
        (applicant.id && applicant.id.toString().toLowerCase().includes(searchTerm)) ||
        (applicant.user_name && applicant.user_name.toLowerCase().includes(searchTerm)) ||
        (applicant.cnic && applicant.cnic.toLowerCase().includes(searchTerm)) ||
        (applicant.phone && applicant.phone.toLowerCase().includes(searchTerm)) ||
        (applicant.allowed_by && applicant.allowed_by.toLowerCase().includes(searchTerm))
      );
    });

    setAllApplicants(filteredApplicants);
  };

  const handleExportCSV = () => {
    if (allApplicants.length === 0) return;
    
    // Define the headers for the CSV
    const headers = [
      "Sr.No",
      "Application ID",
      "User ID", 
      "User Name",
      "CNIC",
      "Phone",
      "Is Allowed",
      "Allowed By",
      "Created At"
    ];

    // Map the rows to include all necessary fields
    const data = allApplicants.map((applicant, index) => ({
      serial: index + 1,
      id: applicant.id,
      user_id: applicant.user_id,
      user_name: applicant.user_name,
      cnic: applicant.cnic,
      phone: applicant.phone,
      is_allowed: applicant.is_allowed ? "Yes" : "No",
      allowed_by: applicant.allowed_by,
      createdAt: applicant.createdAt
    }));

    // Convert to CSV format
    const csv = Papa.unparse([headers, ...data.map((row) => Object.values(row))]);

    // Create a blob and download the CSV
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = "AllowedApplicantsReport.csv";
    link.click();
  };

  // API calls
  const fetchAllCampaigns = async () => {
    try {
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };
      const response = await fetch(BASE_URL + `/campaign/get_campaigns`, requestOptions);
      const data = await response.json();
      setAllCampaigns(data.campaigns);
      
      // Auto-select first active campaign if userDepartment exists
      if (userDepartment && data.campaigns && data.campaigns.length > 0) {
        const activeCampaigns = data.campaigns.filter(camp => camp.status === 1);
        if (activeCampaigns.length > 0) {
          const firstActiveCampaign = activeCampaigns[0];
          const newSelectedCampaign = {
            value: firstActiveCampaign.id,
            label: firstActiveCampaign.name
          };
          
          setSelectedCampaign(newSelectedCampaign);
        }
      }
    } catch (error) {
      console.log("Error fetching campaigns:", error);
    }
  };

  const getCampaignApplicants = async () => {
    // Check if campaign is selected
    if (!selectedCampaign) {
      toast.error("Please select a campaign");
      return;
    }

    setLoading(true);
    
    try {
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await fetch(
        BASE_URL + `/campaign/get_campaign_applicants/${selectedCampaign.value}`,
        requestOptions
      );
      const result = await response.json();
      
      if (response.ok) {
        const applicants = result || [];
        setAllApplicants(applicants);
        setOriginalApplicants(applicants);
        
        toast.success(`${applicants.length} applicants found for the selected campaign`);
      } else {
        toast.error(result.error || "Failed to fetch campaign applicants");
        setAllApplicants([]);
        setOriginalApplicants([]);
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error("An error occurred while fetching campaign applicants");
      setAllApplicants([]);
      setOriginalApplicants([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle campaign selection change
  const handleCampaignChange = (selectedOption) => {
    setSelectedCampaign(selectedOption);
  };

  // Table Configuration
  const columns = [
    { Header: "Sr.No", accessor: "serial", align: "center" },
    { Header: "User ID", accessor: "user_id", align: "center" },
    { Header: "User Name", accessor: "user_name", align: "center" },
    { Header: "CNIC", accessor: "cnic", align: "center" },
    { Header: "Phone", accessor: "phone", align: "center" },
    { Header: "Allowed Status", accessor: "allowed_status", align: "center" },
    { Header: "Allowed By", accessor: "allowed_by", align: "center" },
    { Header: "Created At", accessor: "created_at", align: "center" },
  ];

  const rows = allApplicants.length > 0
    ? allApplicants.map((applicant, index) => ({
        serial: index + 1,
        user_id: applicant.user_id,
        user_name: applicant.user_name || "N/A",
        cnic: applicant.cnic || "N/A",
        phone: applicant.phone || "N/A",
        allowed_status: applicant.is_allowed ? (
          <MDBox ml={-1}>
            <MDBadge
              badgeContent="Allowed"
              color="success"
              variant="gradient"
              size="sm"
            />
          </MDBox>
        ) : (
          <MDBox ml={-1}>
            <MDBadge
              badgeContent="Not Allowed"
              color="error"
              variant="gradient"
              size="sm"
            />
          </MDBox>
        ),
        allowed_by: applicant.allowed_by || "N/A",
        created_at: applicant.createdAt ? new Date(applicant.createdAt).toLocaleDateString() : "N/A",
      }))
    : [
        {
          serial: null,
          id: null,
          user_id: null,
          user_name: null,
          cnic: null,
          phone: null,
          allowed_status: null,
          allowed_by: null,
          created_at: null,
          actions: null,
        },
      ];

  // Initial data loading
  useEffect(() => {
    fetchAllCampaigns();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ToastContainer />
      <MDBox pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card className="mb-4">
              <MDBox p={3}>
                <MDTypography variant="h5" fontWeight="medium" mb={2}>
                  Allowed Applicant Filters
                </MDTypography>
                <Grid container spacing={2}>
                  {/* Campaign Filter */}
                  <Grid item xs={12} md={6}>
                    <MDTypography variant="subtitle2" fontWeight="light" mb={1}>
                      Campaign
                    </MDTypography>
                    <Select
                      name="campaign_id"
                      options={allCampaigns.map((camp) => ({
                        value: camp.id,
                        label: camp.name,
                      }))}
                      className="basic-select"
                      classNamePrefix="select"
                      placeholder="Select Campaign"
                      value={selectedCampaign}
                      onChange={handleCampaignChange}
                      isClearable
                    />
                  </Grid>

                  {/* Action Buttons */}
                  <Grid item xs={12} display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={getCampaignApplicants}
                      disabled={loading}
                      sx={{ mt: 2 }}
                      style={{color: "white"}}
                    >
                      {loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        "Fetch Applicants"
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>

            <Card>
              <MDBox
                mx={0}
                mt={0}
                py={2}
                px={2}
                variant="gradient"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={1}
                  sx={{ width: "100%" }}
                >
                  <MDTypography variant="h6" fontWeight="medium">
                    Allowed Applicants Report
                  </MDTypography>

                  <Stack direction="row" spacing={2}>
                    <Paper component="form" className="search-paper">
                      <InputBase
                        sx={{ ml: 1, flex: 1, fontSize: "16px" }}
                        placeholder="Search"
                        inputProps={{ "aria-label": "search allowed applicants" }}
                        value={searchKeyword}
                        onChange={handleSearchKeywordChange}
                      />
                      <SearchIcon className="search-icon" />
                    </Paper>
                  {hasAdmissionPermission("AllowedApplicantReport", "EXPORT CSV") && (
                    <Button
                      variant="contained"
                      color="primary"
                      endIcon={<DocumentScannerIcon />}
                      onClick={handleExportCSV}
                      style={{color: "white"}}
                    >
                      Export CSV
                    </Button>
                  )}
                  </Stack>
                </Stack>
              </MDBox>
              
              <MDBox pt={3} pb={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" p={5}>
                    <CircularProgress />
                  </MDBox>
                ) : allApplicants && allApplicants.length > 0 ? (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                ) : (
                  <MDBox display="flex" flexDirection="column" alignItems="center" p={5}>
                    <img
                      src={nodata}
                      style={{
                        width: "20%",
                        height: "20%",
                      }}
                      alt="No Data"
                    />
                    <MDTypography variant="body1" mt={2}>
                      No allowed applicants found. Please select a campaign and try again.
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
};

export default AllowedApplicantReport;